import { useEffect, useRef, useState } from 'react'
import { useForm } from '@inertiajs/react'
import downscaleImage from '../downscale_image'

function today() {
  return new Date().toISOString().slice(0, 10)
}

const MAX_IMAGE_MB = 20
const MAX_VIDEO_MB = 200

// "TIFF", "HEIC" — whatever the person is actually looking at in their file picker.
function describe(file) {
  const fromName = file.name?.match(/\.(\w+)$/)?.[1]
  return (fromName || file.type?.split('/')[1] || 'these').toUpperCase()
}

export default function UploadDialog({ open, onClose }) {
  const [preview, setPreview] = useState(null)
  const [tooBig, setTooBig] = useState(null)
  const [dragging, setDragging] = useState(false)
  const [preparing, setPreparing] = useState(false)
  const fileInput = useRef(null)

  const { data, setData, post, processing, progress, errors, reset, clearErrors } = useForm({
    image: null,
    caption: '',
    taken_on: today(),
    favorite: false,
  })

  useEffect(() => {
    if (!open) return

    function onKeyDown(event) {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  useEffect(() => {
    if (!data.image) {
      setPreview(null)
      return
    }

    const objectUrl = URL.createObjectURL(data.image)
    setPreview(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [data.image])

  if (!open) return null

  async function pickFile(file) {
    if (!file) return
    clearErrors()
    setTooBig(null)

    const isVideo = file.type.startsWith('video/')

    // Shrink stills to what the album actually displays. Videos go up as they are.
    let chosen = file
    let undecodable = false
    if (!isVideo) {
      setPreparing(true)
      try {
        ;({ file: chosen, undecodable } = await downscaleImage(file))
      } finally {
        setPreparing(false)
      }
    }

    // Check the size we are really about to send, so nobody waits through a long
    // upload just to be rejected at the far end.
    const limitMb = isVideo ? MAX_VIDEO_MB : MAX_IMAGE_MB

    if (chosen.size > limitMb * 1024 * 1024) {
      const mb = (chosen.size / 1024 / 1024).toFixed(0)

      // Reporting "too big" for a format the browser cannot open sends people off shrinking a
      // file that would still be rejected. Name the real problem instead.
      setTooBig(
        undecodable
          ? `This browser can't open ${describe(file)} files, so it couldn't shrink it — and at ${mb}MB it's over the ${limitMb}MB limit. Save it as a JPEG and try again.`
          : `That ${isVideo ? 'video' : 'photo'} is ${mb}MB — the limit is ${limitMb}MB.`,
      )
      setData('image', null)
      return
    }

    setData('image', chosen)
  }

  function submit(event) {
    event.preventDefault()
    post('/photos', {
      forceFormData: true,
      onSuccess: () => {
        reset()
        onClose()
      },
    })
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add a photo"
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(event) => event.stopPropagation()}
        className="card-cute animate-pop-in max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-b-none p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:rounded-3xl sm:p-6 sm:pb-6"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-2xl font-extrabold text-ink">Add to the album 📸</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="cursor-pointer text-2xl leading-none text-ink-soft/60 hover:text-ink"
          >
            ×
          </button>
        </div>

        <div
          onDragOver={(event) => {
            event.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault()
            setDragging(false)
            pickFile(event.dataTransfer.files?.[0])
          }}
          onClick={() => fileInput.current?.click()}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-3xl border-4 border-dashed p-6 text-center transition ${
            dragging ? 'border-blush-400 bg-blush-50' : 'border-blush-200 bg-white/60 hover:border-blush-300'
          }`}
        >
          {preparing ? (
            <>
              <span className="animate-float-slow text-5xl" aria-hidden="true">
                🍼
              </span>
              <p className="mt-2 font-display font-bold text-ink">Getting it ready…</p>
              <p className="text-sm text-ink-soft">Shrinking the photo so it uploads quickly</p>
            </>
          ) : preview ? (
            data.image?.type?.startsWith('video/') ? (
              <video
                src={preview}
                controls
                playsInline
                className="max-h-56 rounded-2xl border-2 border-white bg-black shadow"
              />
            ) : (
              <img src={preview} alt="Preview" className="max-h-56 rounded-2xl border-2 border-white shadow" />
            )
          ) : (
            <>
              <span className="text-5xl" aria-hidden="true">
                🖼️
              </span>
              <p className="mt-2 font-display font-bold text-ink">Drop a photo or video here</p>
              <p className="text-sm text-ink-soft">or tap to choose one</p>
            </>
          )}

          <input
            ref={fileInput}
            type="file"
            accept="image/*,video/*"
            className="hidden"
            onChange={(event) => pickFile(event.target.files?.[0])}
          />
        </div>

        {tooBig && <p className="mt-2 text-sm font-bold text-rose-600">{tooBig}</p>}
        {errors.image && <p className="mt-2 text-sm font-bold text-rose-600">{errors.image}</p>}
        {preview && (
          <button
            type="button"
            onClick={() => {
              setTooBig(null)
              setData('image', null)
            }}
            className="mt-2 cursor-pointer text-sm font-bold text-ink-soft underline hover:no-underline"
          >
            Choose a different file
          </button>
        )}

        <div className="mt-5 space-y-4">
          <div>
            <label htmlFor="caption" className="mb-1.5 block font-display font-bold text-ink-soft">
              Caption
            </label>
            <input
              id="caption"
              type="text"
              value={data.caption}
              onChange={(event) => setData('caption', event.target.value)}
              maxLength={200}
              placeholder="First bath! Not a fan. 🛁"
              className="input-cute"
            />
            {errors.caption && <p className="mt-1 text-sm font-bold text-rose-600">{errors.caption}</p>}
          </div>

          <div>
            <label htmlFor="taken_on" className="mb-1.5 block font-display font-bold text-ink-soft">
              Taken on
            </label>
            <input
              id="taken_on"
              type="date"
              value={data.taken_on}
              onChange={(event) => setData('taken_on', event.target.value)}
              className="input-cute"
            />
            {errors.taken_on && <p className="mt-1 text-sm font-bold text-rose-600">{errors.taken_on}</p>}
          </div>

          <label className="flex cursor-pointer items-center gap-3 rounded-2xl bg-lemon-baby/50 px-4 py-3">
            <input
              type="checkbox"
              checked={data.favorite}
              onChange={(event) => setData('favorite', event.target.checked)}
              className="h-5 w-5 cursor-pointer accent-blush-400"
            />
            <span className="font-display font-bold text-ink">Mark as a favourite ⭐️</span>
          </label>
        </div>

        {progress && (
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-blush-100">
            <div
              className="h-full rounded-full bg-blush-400 transition-all"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
        )}

        <div className="mt-6 flex gap-3">
          <button
            type="submit"
            disabled={processing || preparing || !data.image}
            className="btn-cute flex-1 bg-blush-400 py-3 text-white shadow-lg hover:bg-blush-500"
          >
            {processing ? 'Uploading…' : 'Add to album'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="btn-cute border-2 border-blush-100 bg-white text-ink-soft hover:bg-blush-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}
